import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {
    Card,
    CardContent,
    CircularProgress
} from "@mui/material";
import {Done} from "@mui/icons-material";
import styles from './fileAndFolderTbl.module.css';
import useDrive from "../../hooks/useDrive";
import useCalculator from "../../hooks/useCalculator";
import {LAYOUTS, UPLOAD_STATUS} from "../../config/appConst";
import DriveActionBar from "../DriveActionBar";
import FileBreadcrumbs from "../FileBreadCrumb";
import stlIcon from "../../asset/icons/stl-icon.png";
import dcmIcon from "../../asset/icons/dcm-icon.png";
import pngIcon from "../../asset/icons/png-icon.png";
import unknownIcon from "../../asset/icons/unknown-icon.png";
import {useDropzone} from "react-dropzone";
import useNotification from "../../hooks/useNotification";
import FileUpload from "../FileUpload";
import folderIcon from "../../asset/icons/folder.png";
import homeIcon from "../../asset/icons/home_check.png";
import patientIcon from "../../asset/icons/patient.png";
import {FolderContentGrid} from "./FolderContentGrid";
import {FolderContentList} from "./FolderContentList";

export default function FilesAndFoldersTbl() {
    const {CurrentDrive, uploadStatus, layout} = useDrive();
    const {byteConversion} = useCalculator();
    const {addNotification} = useNotification();

    const [rows, setRows] = useState([]);
    const [latUpdateTime, setLatUpdateTime] = useState(null);
    const [droppedFile, setDroppedFile] = useState(new Blob());

    const onDrop = useCallback((acceptedFiles) => {
        acceptedFiles.forEach((file) => {
            // console.log(file);
            setDroppedFile(file);
        })
    }, [])

    const {getRootProps, getInputProps, isDragActive, fileRejections} = useDropzone({
        onDrop,
        noClick: true,
        multiple: false,
        maxFiles: 1
    })

    useEffect(() => {
        if (CurrentDrive) {
            const {drive, sharedDrive, driveType} = CurrentDrive;
            // console.log("sharedDrive",sharedDrive)
            // console.log("sharedDrive",driveType)

            if (driveType === "shared") {
                // console.log("sharedDrive", sharedDrive.Files)
                loadRows(sharedDrive);

            } else {
                loadRows(drive);

            }
        }
    }, [CurrentDrive, uploadStatus])

    useEffect(() => {
        if (fileRejections && fileRejections.length > 0) {
            try {
                const {errors} = fileRejections[0]
                // console.log(fileRejections);
                // console.log(errors);
                addNotification({
                    type: 'error',
                    message: errors[0]["message"]
                });
            } catch (e) {
                addNotification({
                    type: 'error',
                    message: 'Something went wrong!'
                });
            }
        }
    }, [fileRejections])

    const loadRows = ({Files, Folders}) => {
        if (!Folders) {
            return
        }
        let rowsCp = [];
        Folders.forEach((f, i) => {
            const name = f["Prefix"].replace('/', '');

            if (name.trim() === '') {
                rowsCp.push({
                    Key: name,
                    LastModified: '',
                    Size: '',
                    id: "fo_" + i,
                    icon: <img src={homeIcon} className={styles.folderIcon} alt={"icon"}/>,
                    iconSrc: homeIcon,
                    type: 'folder'
                })
            } else if (name.trim() === 'Patients') {
                rowsCp.push({
                    Key: name,
                    LastModified: '',
                    Size: '',
                    id: "fo_" + i,
                    icon: <img src={patientIcon} className={styles.folderIcon} alt={"icon"}/>,
                    iconSrc: patientIcon,
                    type: 'folder'
                })
            } else {
                rowsCp.push({
                    Key: name,
                    LastModified: '',
                    Size: '',
                    id: "fo_" + i,
                    icon: <img src={folderIcon} className={styles.folderIcon} alt={"icon"}/>,
                    iconSrc: folderIcon,
                    type: 'folder'
                })
            }
        });
        switch (uploadStatus) {
            case UPLOAD_STATUS.UPLOADING :
                rowsCp.push({
                    Key: 'Uploading a file',
                    LastModified: '',
                    Size: '',
                    id: UPLOAD_STATUS.UPLOADING,
                    icon: <CircularProgress className={styles.iconUpload}/>,
                    type: UPLOAD_STATUS.UPLOADING
                })
                break;
            case UPLOAD_STATUS.COMPLETING:
                rowsCp.push({
                    Key: 'File uploaded',
                    LastModified: '',
                    Size: '',
                    id: UPLOAD_STATUS.COMPLETING,
                    icon: <Done className={styles.iconUpload} color={"success"}/>,
                    type: UPLOAD_STATUS.COMPLETING
                });
                break;
            case UPLOAD_STATUS.FILE_COPPING:
                rowsCp.push({
                    Key: 'File copy in progress',
                    LastModified: '',
                    Size: '',
                    id: UPLOAD_STATUS.FILE_COPPING,
                    icon: <CircularProgress className={styles.iconUpload}/>,
                    type: UPLOAD_STATUS.FILE_COPPING
                });
                break;
            case UPLOAD_STATUS.FILE_MOVING:
                rowsCp.push({
                    Key: 'File move in progress',
                    LastModified: '',
                    Size: '',
                    id: UPLOAD_STATUS.FILE_MOVING,
                    icon: <CircularProgress className={styles.iconUpload}/>,
                    type: UPLOAD_STATUS.FILE_MOVING
                })
                break;
            case UPLOAD_STATUS.FOLDER_COPPING:
                rowsCp.push({
                    Key: 'Folder copy in progress',
                    LastModified: '',
                    Size: '',
                    id: UPLOAD_STATUS.FOLDER_COPPING,
                    icon: <CircularProgress className={styles.iconUpload}/>,
                    type: UPLOAD_STATUS.FOLDER_COPPING
                })
                break;
            case UPLOAD_STATUS.FOLDER_MOVING:
                rowsCp.push({
                    Key: 'Folder move in progress',
                    LastModified: '',
                    Size: '',
                    id: UPLOAD_STATUS.FOLDER_MOVING,
                    icon: <CircularProgress className={styles.iconUpload}/>,
                    type: UPLOAD_STATUS.FOLDER_MOVING
                })
                break;
        }

        Files.forEach((fi, i) => {
            const name = fi["Key"].replace('/', '');
            const src = getIcon(name);
            rowsCp.push({
                path: CurrentDrive.parent ?  CurrentDrive.parent + '/' + name : name,
                Key: name,
                LastModified: fi['LastModified'],
                Size: byteConversion(fi["Size"], 2),
                id: "fi_" + i,
                icon: <img src={src} width={"25px"} alt={"icon"}/>,
                type: 'file',
                iconSrc: src
            })
        });

        setRows(rowsCp);
    }

    const getIcon = name => {
        const x = name.split(".");
        switch (x.pop()) {
            case "stl":
                return stlIcon;
            case "dcm":
                return dcmIcon;
            case "png":
                return pngIcon;
            default:
                return unknownIcon;
        }
    }

    const loadLayout = () => {
        switch (layout) {
            case LAYOUTS.LIST:
                return <FolderContentList rows={rows}/>
            case LAYOUTS.GRID:
                return <FolderContentGrid rows={rows}/>
            default:
                return <FolderContentList rows={rows}/>
        }
    }
    return (
        <Card elevation={0}>
            <div className={styles.actionContainer}>

                {
                    CurrentDrive ? <DriveActionBar setLatUpdateTime={setLatUpdateTime}/> : ''
                }
            </div>
            <CardContent  {...getRootProps()}>
                <div className={styles.breadcrumbContainer}>
                    <FileBreadcrumbs/>
                </div>
                <input {...getInputProps()} />
                <div className={isDragActive ? styles.dropZoneActive : styles.fileTableContainer}>
                    {loadLayout()}
                    {CurrentDrive
                        ? <FileUpload  droppedFile={droppedFile} hideUploadButton={true}/>
                        : console.log("no parent", CurrentDrive)}
                </div>
                {/* <div className={styles.showMeContainer}>
                    <FileExpandView/>
                </div> */}
            </CardContent>
        </Card>
    );
}
